import uniqBy from 'lodash.uniqby'
import {
  batchEditRectangle,
  batchMoveSingleShareToNameSurn,
  cloneElement,
  createFieldValue,
  createStandardizedFieldValue,
  dispatchSingleSuper,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  getPersonIdsForField,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { R2AppName } from '../../../../../../shared/services/config'

export const getPersonsFromField = ({
  currentFieldsOnHighlights,
  selectedPersonId,
  images,
  activeField,
  isNewPerson,
  isNewField,
}) => {
  const sharedElementsCopy = [...currentFieldsOnHighlights]
  if (!isNewField) {
    // Making sure that the selected field is included so that persons sharing it are selectable as well
    const shareableForField = activeField?.typeData?.singleSuper
      ? getNestedOrFloatingElementById(images, activeField?.superElements[0]?.id)
      : activeField
    sharedElementsCopy.unshift(shareableForField)
  }
  let persons = sharedElementsCopy?.reduce((acc, sharedElement) => {
    const personIds = getPersonIdsForField(sharedElement, images)
    const personsFromElement = personIds.map((personId) => getNestedOrFloatingElementById(images, personId))
    acc = acc.concat(...personsFromElement)
    return acc
  }, [])

  if (isNewField && !isNewPerson) persons.unshift(getNestedSubelementsById(images, selectedPersonId)) // WIP
  persons = uniqBy(persons, 'id')
  persons.sort((a, b) => {
    if (a.id === selectedPersonId) return -1
    if (b.id === selectedPersonId) return 1
    return 0
  })

  return persons.flat() || []
}

export const getPersonsByArk = ({ allPersonsSharingField, isNewPerson, selectedPersonId, currentImageArk }) => {
  if (isNewPerson) allPersonsSharingField.unshift({ id: selectedPersonId, partitionKey: currentImageArk })

  return allPersonsSharingField?.reduce(
    (acc, person) => {
      if (!acc[person.partitionKey]) {
        acc[person.partitionKey] = [person]
      } else {
        acc[person.partitionKey].push(person)
      }
      return acc
    },
    {} /* e.g. { '3:1:etc': [...personsOnThisImage] }, '3:1:etc': [...personsOnOtherImage] } */
  )
}

export const getFormDefaultValues = ({ personIds }) => {
  const defaultValues = {}
  personIds.forEach((personId) => {
    defaultValues[personId] = true
  })

  return defaultValues
}

export const getFieldText = (fieldValue) =>
  typeof fieldValue === 'object' ? fieldValue.standard?.standardizedText || fieldValue.originalText : fieldValue

/**
 * Two tasks:
 * Update the field id of ADD_RECT dispatches from the useSlsHighlights hook, adding any rects that are part of the current edit
 * Move all rects already saved on fieldInEdit to the new field
 * @param {Array} originalHighlightDispatches - dispatches generated by the useSlsHighlights hook
 * @param {Object} fieldInfoForDispatches - id and partitionKey of new linked field being created
 * @param {Object} fieldInEdit - the field that was edited with the highlight panel
 * @returns {*[]}
 */
function addUpdatedHighlightDispatches(originalHighlightDispatches, fieldInfoForDispatches, fieldInEdit) {
  // The useSlsHighlightsHooks doesn't have a way to get the recently created field id for the ADD_RECT dispatch so we need to update it here
  const updatedHighlightDispatches = originalHighlightDispatches.map((originalDispatch) => {
    if (originalDispatch.type === 'ADD_RECT') {
      return {
        ...originalDispatch,
        value: { ...originalDispatch.value, id: fieldInfoForDispatches.id },
      }
    }
    return originalDispatch
  })

  const highlightDispatches = [...updatedHighlightDispatches]

  // Move any rects to the new field that existed on the old field
  fieldInEdit?.rects?.forEach((rect) => {
    highlightDispatches.push(
      ...batchEditRectangle({
        oldFieldId: fieldInEdit.id,
        fieldId: fieldInfoForDispatches.id,
        rect,
        oldRect: rect,
        partitionKey: fieldInfoForDispatches.partitionKey,
        oldPartitionKey: fieldInEdit.partitionKey,
      })
    )
  })

  return highlightDispatches
}

export const handleSubmitLinkFieldsViaHighlights = ({
  values,
  linkedFieldValue,
  activeFieldInfo,
  isNewField,
  originalDispatches,
  originalHighlightDispatches,
  images,
  fieldInEdit,
  sharedElements,
  selectedPersonId,
  involvedPersons,
}) => {
  // The initial dispatches will be different for a new field and an existing field
  let dispatches = []
  let fieldInfoForDispatches = {}
  let subElementInfoForDispatches = {}
  if (isNewField) {
    // These dispatches are set on the preceeding edit panel when this panel enters the stack
    dispatches = [...originalDispatches]
    // Edit the ADD_FV dispatch to use the updated field value
    const addFVDispatch = dispatches.find((dispatch) => dispatch.type === 'ADD_FV')
    if (linkedFieldValue.standard) {
      addFVDispatch.value.fieldValue = createStandardizedFieldValue(
        linkedFieldValue,
        activeFieldInfo.fieldType,
        addFVDispatch.value.fieldValue.attribution
      )
    } else {
      addFVDispatch.value.fieldValue.origValue.text =
        typeof linkedFieldValue === 'object' ? linkedFieldValue.originalText : linkedFieldValue
    }
    // Get the field that is receiving the highlight
    fieldInfoForDispatches = {
      id: activeFieldInfo?.id,
    }
    // Get the shareable-level element
    const createNewFieldDispatch = dispatches.find((dispatch) => dispatch?.value?.elementType === 'FIELD')
    if (dispatchSingleSuper(createNewFieldDispatch)) {
      const addSubDispatch = dispatches.find(
        (dispatch) => dispatch.type === 'ADD_SUB' && dispatch.value.subId === fieldInfoForDispatches.id
      )
      const createSuperDispatch = dispatches.find(
        (dispatch) => dispatch.type === 'CREATE_ELEMENT' && dispatch.value.id === addSubDispatch.value.superId
      )
      if (createSuperDispatch) subElementInfoForDispatches = createSuperDispatch.value
      else subElementInfoForDispatches = getNestedOrFloatingElementById(images, addSubDispatch.value.superId)
    } else subElementInfoForDispatches = createNewFieldDispatch.value
  } else {
    // Start dispatches by cloning the target field and its shareable parent element if needed
    dispatches.push(cloneElement(fieldInEdit))
    const clonedField = dispatches[0].value
    // Updating the field value
    const attribution = {
      comment: '',
      reasonId: INDEXED_INCORRECTLY,
      app: R2AppName,
    }
    let fieldValue = createFieldValue(
      typeof linkedFieldValue === 'object' ? linkedFieldValue.originalText : linkedFieldValue,
      attribution
    )
    if (linkedFieldValue.standard) {
      fieldValue = createStandardizedFieldValue(linkedFieldValue, activeFieldInfo.fieldType, attribution)
    }
    dispatches.push({
      type: 'ADD_FV',
      value: {
        // eslint-disable-next-line no-warning-comments
        // TODO: Handle resource parameter for sex and potentially others
        fieldValue,
        id: clonedField.id,
        partitionKey: clonedField.partitionKey,
      },
    })
    // Between the clonedField and its potential parent, get the right subElement
    subElementInfoForDispatches = clonedField
    if (fieldInEdit?.typeData?.singleSuper) {
      const shareableElementForField = getNestedOrFloatingElementById(images, fieldInEdit?.superElements[0]?.id)
      dispatches.push(cloneElement(shareableElementForField))
      subElementInfoForDispatches = dispatches[2].value
      dispatches.push({
        type: 'ADD_SUB',
        value: {
          superPartitionKey: subElementInfoForDispatches.partitionKey,
          subPartitionKey: clonedField.partitionKey,
          superId: subElementInfoForDispatches.id,
          subId: clonedField.id,
          elementType: subElementInfoForDispatches.elementType,
        },
      })
      if (!sharedElements.includes(shareableElementForField)) sharedElements.push(shareableElementForField)
    } else if (!sharedElements.includes(fieldInEdit)) {
      sharedElements.push(fieldInEdit)
    }
    fieldInfoForDispatches = clonedField
  }

  dispatches = dispatches.concat(
    addUpdatedHighlightDispatches(originalHighlightDispatches, fieldInfoForDispatches, fieldInEdit)
  )

  // Get the list of selected person ids
  const selectedPersonIds = []
  Object.entries(values)
    .filter(([key]) => key.startsWith('1:1:'))
    .map(([key, value]) => {
      if (value) selectedPersonIds.push(key)
      return null
    })
  sharedElements.forEach((sharedElement) => {
    // On each superElement of the sharedElement, replace the sharedElement with the fieldInEdit if the associated person is selected
    if (sharedElement?.superElements) {
      sharedElement.superElements.forEach((superElement) => {
        const expandedSuperElement = getNestedOrFloatingElementById(images, superElement.id)
        // We only need to move the subelement if the associated person has been selected
        if (expandedSuperElement.superElements) {
          if (expandedSuperElement.elementType === 'NAME_SURNAME' && expandedSuperElement.superElements.length > 1) {
            expandedSuperElement.superElements.forEach((nameSuperElement) => {
              // Trying to share at the NAME_SURN level, but this element shares at the NAME_SURNAME level, so we will change it
              dispatches = dispatches.concat(
                batchMoveSingleShareToNameSurn(nameSuperElement, expandedSuperElement, subElementInfoForDispatches)
              )
            })
          } else if (selectedPersonIds.includes(expandedSuperElement.personId)) {
            // Create a dispatch to remove the sharedElement from its superElement
            dispatches.push({
              type: 'REM_SUB',
              value: {
                superPartitionKey: expandedSuperElement.partitionKey,
                subPartitionKey: sharedElement.partitionKey,
                superId: expandedSuperElement.id,
                subId: sharedElement.id,
                elementType: expandedSuperElement.elementType,
              },
            })
            // Create a dispatch to add the fieldInEdit to the superElement
            dispatches.push({
              type: 'ADD_SUB',
              value: {
                superPartitionKey: expandedSuperElement.partitionKey,
                subPartitionKey: subElementInfoForDispatches.partitionKey,
                superId: expandedSuperElement.id,
                subId: subElementInfoForDispatches.id,
                elementType: expandedSuperElement.elementType,
              },
            })
          }
        }
      })
    }
  })

  // Clear out the localizedNames values for the selected persons to force a refresh
  const uniqueSelectedPersonIds = [...new Set([...selectedPersonIds, selectedPersonId])]
  const localizeNameDispatches = uniqueSelectedPersonIds.map((personId) => {
    const person = involvedPersons?.find((p) => p.id === personId)
    return {
      type: 'SET_PERSON_LOCALIZED_NAME',
      value: {
        personId,
        localizedName: '',
        partitionKey: person.partitionKey,
      },
    }
  })

  dispatches = [...dispatches, ...localizeNameDispatches]

  return dispatches
}

/**
 * Helper function to get field value using the same logic as SelectSlsField
 * Extracts field values from different field structures consistently
 * @param {Object} field - The field object to extract value from
 * @param {Object} state - The SLS state context containing type information
 * @returns {string} The extracted field value or empty string
 */
export const getFieldValue = (field, state) => {
  let result = ''

  if (field?.value) {
    if (field.value?.origValue?.text) {
      result = field.value.origValue.text
    } else if (field.value?.primaryText) {
      result = field.value.primaryText
    } else if (field.value.value) {
      result = field.value.value // From a useFieldArray managed field
    } else if (!state.types?.fields.find((type) => type.fieldType === field.value)) {
      result = field.value
    }
  } else if (field?.fieldValues?.[0]?.origValue?.text) {
    result = field.fieldValues[0].origValue.text
  } else {
    result = getMostRecentFieldValue(field) || ''
  }

  return result
}

/**
 * Filters highlights on the indicated image down to only those related to the currently selected field
 *
 * @param {String} imageArk The ark id of the image on which we need highlights
 * @param {Array} editHighlights The array of highlights in state that we're filtering down
 * @param {String} selectedPersonId The currently selected person ID
 * @param {String} fieldType The field type to filter by
 *
 * @returns {Array} An array of highlights on the image that relate to the currently selected field
 */
export const getHighlightsForField = (imageArk, editHighlights, selectedPersonId, fieldType) => {
  return editHighlights
    .filter((highlight) => highlight.personIds.includes(selectedPersonId))
    .filter((f) => fieldType === f.fieldType)
    .filter((h) => h?.imageId === imageArk)
}

/**
 * Creates field info list for select components from highlights
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.highlightsOnSelectedField - Highlights on the current field
 * @param {Array} params.splitScreenHighlightsOnSelectedField - Highlights on split screen field
 * @param {Object} params.newFieldForHighlight - The new field for highlight
 * @param {Array} params.images - Images from state
 * @param {Array} params.fieldTypes - Field types from state
 * @param {Object} params.state - SLS state context
 *
 * @returns {Array} Field info array for select lists
 */
export const createFieldInfoForSelectList = ({
  highlightsOnSelectedField,
  splitScreenHighlightsOnSelectedField,
  newFieldForHighlight,
  images,
  fieldTypes,
  state,
}) => {
  // We initialize the array with the currently selected field, which is the new field asking to use the highlight
  const fieldInfo = [
    {
      ...newFieldForHighlight,
      value: getFieldValue(newFieldForHighlight, state),
    },
  ]

  // This helper function generates objects representing the information we need from the fields on the highlights
  const getFieldInfoFromHighlights = (highlights) => {
    return highlights.forEach((h) => {
      if (h?.originalHighlight?.personIds?.length > 0) {
        const fieldOnHighlight = getNestedOrFloatingElementById(images, h?.originalHighlight?.fieldId)
        const value = getFieldValue(fieldOnHighlight, state)
        const alreadyHasInfo = fieldInfo.find(
          (f) =>
            f.id === h?.originalHighlight?.fieldId ||
            (f.fieldType === h.originalHighlight?.fieldType && f.value === value)
        )
        if (!alreadyHasInfo) {
          const info = fieldOnHighlight
            ? { ...fieldOnHighlight, value }
            : {
                id: h?.originalHighlight?.fieldId,
                elementType: 'FIELD',
                fieldType: h?.originalHighlights?.fieldType,
                label: fieldTypes?.find((type) => type.fieldType === h?.originalHighlight?.fieldType)?.displayLabel,
                value,
              }
          fieldInfo.push(info)
        }
      }
    })
  }

  getFieldInfoFromHighlights(highlightsOnSelectedField)
  getFieldInfoFromHighlights(splitScreenHighlightsOnSelectedField)

  return fieldInfo
}

/**
 * Gets pluralized instructions text for field selection
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.fieldInfoForSelectList - Field info list
 * @param {Object} params.newFieldForHighlight - New field for highlight
 * @param {boolean} params.isReassignFlow - Whether this is a reassign flow
 * @param {Function} params.t - Translation function
 *
 * @returns {Object} Object with header and instructions text
 */
export const getPluralizationText = ({ fieldInfoForSelectList, newFieldForHighlight, isReassignFlow, t }) => {
  let header
  let instructions

  if (isReassignFlow) {
    header = t(
      'review-highlight-linked-fields.select-field-instructions.header-reassign',
      'Select the correct transcription'
    )
    instructions = t(
      'review-highlight-linked-fields.select-field-instructions.text-reassign',
      'The selected highlight can only be linked to one field at a time. Pick the transcription to use.'
    )
  } else if (fieldInfoForSelectList.length > 1) {
    header = t(
      'review-highlight-linked-fields.select-field-instructions.header-plural',
      'Highlights for {fieldType} fields.',
      { fieldType: newFieldForHighlight.label }
    )
    instructions = t(
      'review-highlight-linked-fields.select-field-instructions.text-plural',
      'The selected highlights are linked with the fields below. Pick the transcription to use.'
    )
  } else {
    header = t(
      'review-highlight-linked-fields.select-field-instructions.header-singular',
      'Highlight for {fieldType} field.',
      { fieldType: newFieldForHighlight.label }
    )
    instructions = t(
      'review-highlight-linked-fields.select-field-instructions.text-singular',
      'The selected highlight is linked with the fields below. Pick the transcription to use.'
    )
  }

  return { header, instructions }
}
